import React, { useState, useEffect } from 'react';

interface File {
  name: string;
  type: string;
  size?: string;
  content?: string;
}

interface FolderWindowProps {
  folderType: 'videos' | 'recycle';
  onFileOpen?: (file: File) => void;
}

const FolderWindow: React.FC<FolderWindowProps> = ({ folderType, onFileOpen }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorContent, setErrorContent] = useState<string | null>(null);

  useEffect(() => {
    fetchFiles();
  }, [folderType]);

  const fetchFiles = async () => {
    try {
      let response, data;

      if (folderType === 'recycle') {
        response = await fetch('/api/desktop/recycle');
        data = await response.json();
        setFiles(data.items || []);
      } else {
        response = await fetch('/api/files/videos');
        data = await response.json();
        setFiles(data.files || []);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDoubleClick = (file: File) => {
    if (folderType === 'recycle') {
      // Show file content or default message
      setErrorContent(file.content || 'This file has been deleted.');
    } else if (onFileOpen) {
      onFileOpen(file);
    }
  };

  const closeError = () => {
    setErrorContent(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{
        background: '#c0c0c0',
        borderBottom: '2px solid #808080',
        padding: '2px 4px',
        display: 'flex',
        gap: '8px',
        fontSize: '11px'
      }}>
        <span style={{ padding: '2px 8px' }}><u>F</u>ile</span>
        <span style={{ padding: '2px 8px' }}><u>E</u>dit</span>
        <span style={{ padding: '2px 8px' }}><u>V</u>iew</span>
        <span style={{ padding: '2px 8px' }}><u>H</u>elp</span>
      </div>

      {/* File list */}
      <div style={{ flex: 1, background: '#fff', padding: '8px', overflow: 'auto' }}>
        {loading ? (
          <div>Loading...</div>
        ) : files.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#666' }}>
            {folderType === 'videos' ? (
              <>
                <p>No videos uploaded yet.</p>
                <p style={{ fontSize: '12px', marginTop: '8px' }}>
                  Upload videos through the admin panel.
                </p>
              </>
            ) : (
              <p>Recycle Bin is empty.</p>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #808080', textAlign: 'left' }}>
                <th style={{ padding: '4px 8px', fontSize: '11px' }}>Name</th>
                <th style={{ padding: '4px 8px', fontSize: '11px' }}>Type</th>
                <th style={{ padding: '4px 8px', fontSize: '11px' }}>Size</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, index) => (
                <tr
                  key={index}
                  style={{
                    background: selectedFile === file.name ? '#000080' : 'transparent',
                    color: selectedFile === file.name ? '#fff' : '#000',
                    cursor: 'pointer'
                  }}
                  onClick={() => setSelectedFile(file.name)}
                  onDoubleClick={() => handleDoubleClick(file)}
                >
                  <td style={{ padding: '4px 8px', fontSize: '11px' }}>
                    {folderType === 'videos' && '🎬'}
                    {folderType === 'recycle' && '📄'}
                    {' '}{file.name}
                  </td>
                  <td style={{ padding: '4px 8px', fontSize: '11px' }}>{file.type}</td>
                  <td style={{ padding: '4px 8px', fontSize: '11px' }}>{file.size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Status bar */}
      <div style={{
        background: '#c0c0c0',
        borderTop: '2px solid #fff',
        padding: '2px 8px',
        fontSize: '11px'
      }}>
        {files.length} object(s)
      </div>

      {/* Error dialog */}
      {errorContent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="window" style={{ width: '400px' }}>
            <div className="title-bar">
              <div className="title-bar-text">Error</div>
            </div>
            <div className="window-body" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '32px' }}>⚠️</span>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 16px 0' }}>{errorContent}</p>
                  <div style={{ textAlign: 'right' }}>
                    <button onClick={closeError}>OK</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderWindow;
